import React, {FC, useEffect, useMemo, useState,  useRef,} from "react";
import {Accordion} from "react-bootstrap";
import * as Yup from "yup";
import { Formik,Form } from "formik";
import {
  ButtonBox,
  ConfirmModelDialogBox,
  DeleteTransactionComponent,
  ErrorValidationBox,
  GenericModelPopupPdfReportViewer,
  GridTable,
  LoadingBox,
  ModelDialogBox,
  PaymentDetails, RefundItemList,
  RegisterTransactionHeader,
  RegisterTransactionItem,
  RegisterTransactionSummary,
  ToastBox,
  TransactionList,
} from "../..";
import {
    DailyTransactionTypeEnum,
    HasFormIdModel, PaymentTypeEnum,
    PrintTypeEnum, RequestActionModel, RowStateEnum, TransactionActionOtherValueResponseModel,
    TransactionDetailResponseModel, UserConfigurationModel, ValidationErrorModel,
} from "../../../models";
import {
    generateGuid,
    getLabelName,
    getPageNameByUrl,
    isArabicCurrentLanguage,
    scrollToDocumentTop,
} from "../../../utils";
import { useSearchParams} from "react-router-dom";
import {
    useTransactionObject,
    useTransactionLValidationErrors,
    useTransactionLToastModel,
    useTransactionLoading,
    useTransactionShowDeleteModal,
    useTransactionPrintObject,
    useTransactionRefundObject,
    useShowPaymentDetailsModel, useShowRefundItemModel, useTransactionResetKey,  useTransactionActivePageKey
} from "../common/hooks/transactions/useTransactionSelectors";
import {useTransactionActions} from "../common/hooks/transactions/useTransactionActions";
import {useTransactionHandlers} from "../common/hooks/transactions/useTransactionHandlers";
import {transactionDetailInitialValues} from "../common/initialValues/initialValues";
import {
    createInitializedTransactionObject,
    isRefundTransaction,
    UpdateTransactionActionUser
} from "../common/businessLogic/transactionBl";
import {
  getTransactionItemsColumnsWithCallback,
  handleRowStyle
} from "../common/uiHelper/dataTableColumns";
import {useTransactionInitializationContext} from "../common/hooks/initializer/useTransactionInitializationContext";
import {useSystemConfigurationsContext} from "../common/hooks/configurations/useSystemConfigurationsContext";
import {
    validateRefundSubmitRequestAdditionalValidation,
    validateSubmitRequest
} from "../common/businessLogic/validationBl";
import {saveTransaction} from "../../../serviceBroker/transactionApiServiceBroker";
import {
    useBeneficiaryList, useCalcTypeList, useCostCenterList, useCurrencyList,
    usePaymentTypeList, useStoreEmployeeList
} from "../common/hooks/initializer/useTransactionInitializationSelectors";
import {useGenericSystemConfigurations, useUserConfiguration} from "../../../hooks";
interface TransactionMainComponentProps extends HasFormIdModel {
  transactionType: DailyTransactionTypeEnum;
}

export const TransactionMainComponent: FC<TransactionMainComponentProps> = ({
  transactionType,
  formId,
}) => {
    //#region state
    const {handleAction} = useTransactionHandlers(transactionType, formId);
    const {isLoaded: isTransactionInitializationLoaded} = useTransactionInitializationContext({
        transactionType,
        formId
    });
    const {isLoaded: isConfigurationLoaded} = useSystemConfigurationsContext({transactionType, formId});
    const isArabic = isArabicCurrentLanguage();
    const object = useTransactionObject();
    const refundObject = useTransactionRefundObject();
    const {
        handleSetObject,
        handleSetShowPaymentDetailsModel,
        handleSetShowRefundItemModel,
        handleSetShowDeleteModal,
        handleSetPrintObject,
        handleSetToastModel,
        handleSetValidationErrors,
        handleSetLoading,
        handleRefreshListTrigger,handleSetResetKey,handleTransactionActivePageKey
    } = useTransactionActions();
    const initialValues = useMemo(() => object ?? transactionDetailInitialValues, [object]);
    const [pageName, setPageName] = useState<string>("");
    const validationErrors = useTransactionLValidationErrors();
    const toastModel = useTransactionLToastModel();
    const loading = useTransactionLoading();
    const showDeleteModal = useTransactionShowDeleteModal();
    const showPaymentDetailsModel = useShowPaymentDetailsModel();
    const showRefundItemModel = useShowRefundItemModel();
    const printObjet = useTransactionPrintObject();
    const beneficiaryList = useBeneficiaryList();
    const paymentTypeList = usePaymentTypeList();
    const calcTypeList = useCalcTypeList();
    const currencyList = useCurrencyList();
    const costCenterList = useCostCenterList();
    const storeEmployeeList = useStoreEmployeeList();
    const {fillData} = useTransactionHandlers(transactionType, formId);
    const resetKey = useTransactionResetKey();
    const activePageKey  = useTransactionActivePageKey();
    const { userConfiguration: { userSetting, companySetting,userTransactionSetting }} = useUserConfiguration(transactionType, handleSetLoading);
    const {genericSystemConfigurations:genericSystemConfiguration, isConfigurationLoaded: isGenericSystemConfigLoaded} = useGenericSystemConfigurations(handleSetLoading);
    const [searchParams] = useSearchParams();
    const activeTransactionDetails = useMemo(() => {
        return (
            object?.TransactionDetaillist?.filter(
                (transaction) => transaction.rowState !== RowStateEnum.Delete
            ) ?? []
        );
    }, [object?.TransactionDetaillist]);
    const columns = useMemo(() => {
        if (!isTransactionInitializationLoaded || !isConfigurationLoaded) return [];
        return [
            ...getTransactionItemsColumnsWithCallback(
                isArabic,
                transactionType,
                handleSetObject,
                object ?? transactionDetailInitialValues,
                companySetting!,
                userTransactionSetting!,
                userSetting!
            ),
        ];
    }, [
        isArabic,
        transactionType,
        //handleSetObject,
        object,
        userTransactionSetting,
        isTransactionInitializationLoaded, isConfigurationLoaded
    ]);
    //#endregion
    //#region refs
    const lookupRefs: Record<string, React.MutableRefObject<any>> = {
        StoreSelectBoxMultiselectRef: useRef<any>(),
        itemCategorySelectBoxMultiselectRef: useRef<any>(),
        itemSelectBoxMultiselectRef: useRef<any>(),
        customerSelectBoxMultiselectRef: useRef<any>(),
        calcTypeSelectBoxMultiselectRef: useRef<any>(),
        paymentTypeSelectBoxMultiselectRef: useRef<any>(),
        paymentTypeForSupplierPaymentSelectBoxMultiselectRef: useRef<any>(),
        currencySelectBoxMultiselectRef: useRef<any>(),
        storeEmployeeSelectBoxMultiselectRef: useRef<any>(),
        costCenterSelectBoxMultiselectRef: useRef<any>(),
        transactionSettingStatusButtonRef: useRef<any>(),
    }
    const transactionSettingStatusButtonRef = useRef<any>();
    //#endregion
    //#region useEffect
    useEffect(() => {
        const initialData = async () => {
            await fillData(searchParams);
        };
        initialData().then(() => {
        });
    }, [searchParams]);
    useEffect(() => {
        setPageName(getPageNameByUrl());
    }, [isArabicCurrentLanguage()]);
    useEffect(() => {
        const key = `${formId}-${transactionType}`;
        if (activePageKey!=null && activePageKey!=""&& activePageKey != key) {
            const run = async () => {
                setPageName(getPageNameByUrl());
                await resetTransaction();
            };
            run().then(() => {});
        }
        handleTransactionActivePageKey(key);
    }, [formId]);
    //#endregion
    //#region formik
    const validationSchema = Yup.object({
        //Code: Yup.string().required("Code is required"),
        //Customer_ID: Yup.number().required("Customer is required"),
    });
    //#endregion
    //#region function
    const createNewTransactionObject = async () => {
        return await createInitializedTransactionObject({
            transactionDetail: {...transactionDetailInitialValues},
            currentTransactionSetting: userTransactionSetting!,
            beneficiaryList: beneficiaryList,
            paymentTypeList: paymentTypeList,
            currencyList: currencyList,
            storeEmployeeList: storeEmployeeList,
            calcTypeList: calcTypeList,
            costCenterList: costCenterList
        });
    };
    const onReset = async () => {
        await resetTransaction();
    }
    const onSubmit = async () => {
        try {
            let request = {...object} as TransactionDetailResponseModel;
            if (!request) {
                console.error('Request object is null or undefined');
                return;
            }
            handleSetLoading(true);
            request.TransactionType = transactionType;
            request.DueDate = request?.PaymentType_ID == PaymentTypeEnum.Delay ? request?.DueDate : null;
            request = UpdateTransactionActionUser(request);
            const userConfiguration: UserConfigurationModel = {
                userSetting,
                companySetting,
                userTransactionSetting,
                userTransactionSettings: [],
                systemModulesConfigurations: [],
                genericSystemConfiguration,
            };
            const errorList = await validateSubmitRequest(request, userConfiguration);
            switch (request.TransactionType) {
                case Number(DailyTransactionTypeEnum.SalesRefund):
                case Number(DailyTransactionTypeEnum.PurchasesRefund):
                    const refundErrorList: ValidationErrorModel[] =
                        await validateRefundSubmitRequestAdditionalValidation(request);
                    if (refundErrorList !== null && refundErrorList.length !== 0) {
                        errorList.push(...refundErrorList);
                    }
                    break;
            }
            if (errorList.length > 0) {
                handleSetValidationErrors(errorList);
                return;
            }
            switch (Number(request.PaymentType_ID)) {
                case Number(PaymentTypeEnum.Multiple):
                    handleSetShowPaymentDetailsModel(true);
                    break;
                default:
                    const response = await saveTransaction(request);
                    if (response.Errors?.length) {
                        handleSetValidationErrors(response.Errors);
                    } else {
                        scrollToDocumentTop();
                        const object = await createNewTransactionObject();
                        handleSetObject({...object});
                        handleSetToastModel({...toastModel, show: true});
                        handleSetValidationErrors([]);
                        handleSetShowDeleteModal(false);
                        handleSetResetKey(resetKey+1);
                        handleRefreshListTrigger(generateGuid());
                    }
                    break;
            }
        } catch (error: any) {
        } finally {
            handleSetLoading(false);
        }
    };
    const resetTransaction = async () => {
        try {
            handleSetLoading(true);
            scrollToDocumentTop();

            const object = await createNewTransactionObject();

            handleSetObject({ ...object });
            handleSetValidationErrors([]);
            handleSetShowDeleteModal(false);
            handleSetResetKey(resetKey + 1);
        } catch (error: any) {
        } finally {
            handleSetLoading(false);
        }
    };
    //#endregion
    //#region html
    return (
        <div style={{position: 'relative'}}>
            <Formik initialValues={transactionDetailInitialValues}
                    validationSchema={validationSchema}
                    onReset={onReset} //
                    onSubmit={onSubmit}>
                <Form>
                    {/*<div ref={validationMessageRef} />*/}
                    {loading && <LoadingBox/>}
                    {printObjet?.show && (
                        <GenericModelPopupPdfReportViewer
                            keys={[{key: "transactionId", value: printObjet?.id || 0}]}
                            type={PrintTypeEnum.Invoice}
                            onCloseEvent={() => {
                                handleSetPrintObject({id: null, show: false});
                            }}
                        />
                    )}
                    {<ErrorValidationBox errors={validationErrors}/>}
                    {toastModel.show && (
                        <ToastBox
                            isShown={toastModel.show}
                            header={toastModel.header}
                            body={toastModel.body}
                            variant={toastModel.variant}
                            delayDuration={toastModel.delayDuration}
                            onCloseEvent={() => {
                                handleSetToastModel({...toastModel, show: false});
                            }}
                        />
                    )}
                    {/*transaction summary*/}
                    <div
                        className="sticky-buttons-container"
                        style={{
                            position: "sticky",
                            top: 60,
                            zIndex: 1,
                            padding: "15px",
                            marginBottom: "6px",
                            background: "rgb(249, 247, 247)",
                            boxShadow: "0px 10px 15px rgba(28, 26, 26, 0.1)",
                            borderRadius: "16px",
                            border: "1px solid  #e3e3e3",
                        }}
                    >
                        <RegisterTransactionSummary transactionType={transactionType} formId={formId}/>
                    </div>
                    {/*transaction header*/}
                    <Accordion defaultActiveKey="0">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>{pageName}</Accordion.Header>
                            <Accordion.Body>
                                <RegisterTransactionHeader
                                    transactionType={transactionType}
                                    formId={formId}
                                    lookupRefs={lookupRefs}
                                    //shouldReset={componentActionType == ComponentActionTypeEnum.Reset}
                                    // onActionEvent={(o: RequestActionModel) => {
                                    //   handleAction(o).then(() => {});
                                    // }}
                                />

                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    {/*register item*/}
                    {!isRefundTransaction(transactionType) && (
                        <Accordion defaultActiveKey="1">
                            <Accordion.Item eventKey="1">
                                <Accordion.Header>
                  <span>
                    {" ".concat(getLabelName("Add Items To"), pageName)}
                  </span>
                                </Accordion.Header>
                                <Accordion.Body>
                                    {transactionType !=
                                        DailyTransactionTypeEnum.ExtractAndDistribute}{" "}
                                    <RegisterTransactionItem
                                        transactionType={transactionType}
                                        formId={formId}
                                        lookupRefs={lookupRefs}
                                    />
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    )}
                    {/*transaction items list*/}
                    {activeTransactionDetails.length > 0 && (
                        <Accordion defaultActiveKey="2">
                            <Accordion.Item eventKey="2">
                                <Accordion.Body>
                                    <>
                                        <div className="item-unit-list">
                                            <div className="section-title">
                                                <span>{getLabelName("Item Transaction")}</span>
                                            </div>
                                            <GridTable
                                                columns={columns}
                                                data={activeTransactionDetails}
                                                totalRows={1000}
                                                currentPage={1}
                                                pageSize={10000}
                                                paginationType="none"
                                                conditionalRowStyles={handleRowStyle(
                                                    activeTransactionDetails || []
                                                )}
                                            />
                                        </div>
                                    </>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    )}
                    {/*payment Details*/}
                    <ModelDialogBox
                        isXCloseButtonVisible={true}
                        isModelVisible={showPaymentDetailsModel}
                        isCloseButtonVisible={false}
                        isEscapeCloseEnabled={true}
                        size="lg"
                        title={getLabelName("payment details")}
                        onCloseEvent={() => {
                            handleSetShowPaymentDetailsModel(false);
                        }}
                    >
                        <PaymentDetails
                            setShowPaymentDetailsModel={handleSetShowPaymentDetailsModel}
                            totalPayment={object?.TotalNet ?? 0}
                            onActionEvent={(o: RequestActionModel) => {
                                handleAction({...o, otherValue: object}).then(() => {
                                });
                            }}
                        />
                    </ModelDialogBox>
                    {/*show refund Details*/}
                    {showRefundItemModel && (
                        <RefundItemList
                            request={refundObject ?? initialValues}
                            onActionEvent={(o: RequestActionModel) => {
                                handleSetShowRefundItemModel(false);
                                handleAction({
                                    ...o,
                                    otherValue: {
                                        object: object,
                                        refs: lookupRefs
                                    } as TransactionActionOtherValueResponseModel
                                }).then(() => {
                                });
                            }}
                        />
                    )}
                    <ButtonBox
                        style={{display: "none"}}
                        ref={transactionSettingStatusButtonRef}
                        onClick={async () => {
                        }}
                    />
                    <ConfirmModelDialogBox
                        isModelVisible={showDeleteModal}
                        onCloseEvent={() => {
                            handleSetShowDeleteModal(false);
                        }}
                    >
                        <DeleteTransactionComponent
                            formId={formId}
                            transactionType={transactionType}
                        />
                    </ConfirmModelDialogBox>
                    <Accordion defaultActiveKey="0">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>{getLabelName("Previous Data")}</Accordion.Header>
                            <Accordion.Body className="TUEFO-header">
                                <TransactionList
                                    transactionType={transactionType}
                                    formId={formId}
                                    //onActionEvent={(o: RequestActionModel) => handleAction(o)}
                                />
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Form>
            </Formik>
        </div>
    );
    //#endregion
};
